import { PayloadAction } from '@reduxjs/toolkit';
import { IPosition, IRotation } from 'types/domain/base';
import { IScenePreview } from 'types/domain/scene';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { SceneEditorSaga } from './saga';
import { ICameraState, updateCameraState, updateControlsState, updateViewerState } from 'core/slices/player';


export interface ISceneEditorState {
  camera: ISceneEditCameraState;
  awaitingCapture: boolean;
  zoom: number;
  edit: IEditState;
  enabled: boolean;
  updated: boolean;
  reset: boolean;
  copy: ICopySettings;
}

export interface IEditState {
  mediaEdit: boolean;
  model: boolean;
  hideText: boolean;
}

export interface ISceneEditCameraState {
  position: IPosition;
  rotation: IRotation;
  save?: boolean;
}

export interface ICopySettings {
  actionId?: number;
  positionId?: number;
}

export const initialState: ISceneEditorState = {
  camera: { position: { id: null, x: 0, y: 0, z: 0 }, rotation: { id: null, x: 0, y: 0, z: 0, w: 0 } },
  awaitingCapture: false,
  zoom: 1,
  edit: {
    mediaEdit: false,
    model: false,
    hideText: false,
  },
  enabled: false,
  updated: false,
  reset: false,
  copy: null,
};

const slice = createSlice({
  name: 'sceneEditor',
  initialState,
  reducers: {
    updateSceneCameraState(state, action: PayloadAction<Partial<ISceneEditCameraState>>) {
      state.camera = { ...state.camera, ...action.payload };
      state.updated = action.payload.save;
    },
    setCameraDefault(state) {
      state.awaitingCapture = true;
      state.updated = false;
    },
    scenePreviewCaptured(state, action: PayloadAction<Partial<IScenePreview>>) {
      state.awaitingCapture = false;
    },
    updateZoom(state, action) {
      state.zoom = action.payload;
    },
    edit(state, action: PayloadAction<Partial<IEditState>>) {
      state.edit = { ...initialState.edit, ...action.payload };
    },
    enable(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload;
    },
    reset(state, action: PayloadAction<boolean>) {
      state.updated = false;
      state.reset = true;
    },
    copySceneSettings(state, action: PayloadAction<ICopySettings>) {
      state.copy = action.payload;
    },
    pasteSceneSettings(state, action: PayloadAction<number>) {},
  },
  extraReducers: builder => {
    builder.addCase(updateViewerState, (state, action) => {
      state.edit = initialState.edit;
    });
    builder.addCase(updateCameraState, (state, action: PayloadAction<Partial<ICameraState>>) => {
      state.updated = action.payload.save;
    });
    builder.addCase(updateControlsState, (state, action) => {
      state.reset = false;
    });
  },
});

export const { actions: sceneEditorActions, reducer } = slice;

export const useSceneEditSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: SceneEditorSaga });
  return { actions: slice.actions };
};
